import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { de } from "date-fns/locale";
import { format } from "date-fns";
import { Mail, MapPin, Calendar as CalendarIcon, Instagram, ExternalLink } from "lucide-react";

function App() {
  const [date, setDate] = useState(new Date());
  // Statische Website - keine Events aus Datenbank
  const events = [];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="App bg-white">
      
      <header
        data-testid="header"
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b-2 border-[#0D5E38]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_099e6719-f7e3-4920-b49f-d93cc57c7b65/artifacts/3khh2k4n_IMG_4928.png"
              alt="Bündnis 90/Die Grünen Logo"
              className="h-12 md:h-16"
              data-testid="header-logo"
            />
          </div>
          <nav className="hidden md:flex gap-6 font-bold text-sm uppercase tracking-wide">
            <button
              onClick={() => scrollToSection("about")}
              className="text-[#0D5E38] hover:text-[#0077FF] transition-colors"
              data-testid="nav-about"
            >
              Über Uns
            </button>
            <button
              onClick={() => scrollToSection("termine")}
              className="text-[#0D5E38] hover:text-[#0077FF] transition-colors"
              data-testid="nav-termine"
            >
              Termine
            </button>
            <button
              onClick={() => scrollToSection("kontakt")}
              className="text-[#0D5E38] hover:text-[#0077FF] transition-colors"
              data-testid="nav-kontakt"
            >
              Kontakt
            </button>
          </nav>
        </div>
      </header>

      <section
        data-testid="hero-section"
        className="relative py-20 md:py-32 bg-[#0D5E38] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <h1
              data-testid="hero-title"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6"
            >
              Grüne in Frankreich vernetzen sich.
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl leading-relaxed"
            >
              Ein informeller Austausch für Grünen-Mitglieder und Nahestehende in der Île-de-France.
            </p>
            <button
              onClick={() => scrollToSection("kontakt")}
              data-testid="hero-cta-button"
              className="bg-[#FBEA0B] text-[#0D5E38] font-bold px-8 py-4 text-lg border-2 border-black shadow-[4px_4px_0px_0px_#000000] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#000000] transition-all uppercase tracking-wide"
            >
              Mehr erfahren
            </button>
          </div>
        </div>
      </section>

      <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-7">
              <h2
                data-testid="about-title"
                className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-[#0D5E38]"
              >
                Über uns
              </h2>
              <div className="text-lg md:text-xl leading-relaxed text-gray-800 space-y-4">
                <p data-testid="about-description-1">
                  Wir sind ein <strong>informeller, privater Zusammenschluss</strong> von Menschen, die Bündnis 90/Die Grünen 
                  nahestehen und in Frankreich leben. Dies ist <strong>keine offizielle Stelle der Partei</strong>, 
                  sondern ein Ort für persönlichen Austausch.
                </p>
                <p data-testid="about-description-2">
                  Unser Fokus liegt auf dem Austausch über <strong>deutsche und europäische Politik</strong>. 
                  Wir treffen uns, um aktuelle politische Entwicklungen zu diskutieren, Meinungen auszutauschen 
                  und uns gegenseitig zu vernetzen – fernab von Deutschland, aber verbunden durch gemeinsame 
                  politische Werte.
                </p>
                <p className="text-sm text-gray-600 italic mt-6">
                  <strong>Hinweis:</strong> Diese Initiative ist unabhängig und hat keine direkte organisatorische 
                  Verbindung zu Bündnis 90/Die Grünen oder deren offiziellen Strukturen.
                </p>
              </div>

              <div className="mt-8 bg-[#F3F4F6] border-l-4 border-[#0D5E38] p-6">
                <h3 className="text-xl font-bold text-[#0D5E38] mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Informelle Stammtische
                </h3>
                <p className="text-gray-800 leading-relaxed" data-testid="stammtisch-info">
                  Wir planen regelmäßige, ungezwungene Treffen in Paris und Umgebung zum Austausch über 
                  aktuelle politische Themen aus Deutschland und Europa. Die Termine werden hier bekannt 
                  gegeben. Alle Interessierten sind herzlich willkommen!
                </p>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="bg-white border-2 border-[#0D5E38] p-8 shadow-[6px_6px_0px_0px_rgba(13,94,56,0.2)]">
                <img
                  src="https://images.unsplash.com/photo-1758520388316-416b732104f1?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Austausch und Diskussion"
                  className="w-full h-64 object-cover border-2 border-[#0D5E38] mb-6"
                />
                <h3 className="text-2xl font-bold text-[#0D5E38] mb-4">Wer kann teilnehmen?</h3>
                <ul className="space-y-3 text-gray-800">
                  <li className="flex items-start gap-2">
                    <span className="text-[#FBEA0B] text-xl">●</span>
                    <span>Mitglieder von Bündnis 90/Die Grünen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FBEA0B] text-xl">●</span>
                    <span>Sympathisanten und politisch Interessierte</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FBEA0B] text-xl">●</span>
                    <span>Alle, die sich über deutsche/europäische Politik austauschen möchten</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="termine"
        data-testid="calendar-section"
        className="py-20 md:py-32 bg-[#F3F4F6]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2
            data-testid="calendar-title"
            className="text-3xl md:text-5xl font-bold tracking-tight mb-12 text-[#0D5E38] text-center"
          >
            Termine & Treffen
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div className="flex justify-center">
              <div className="border-2 border-[#0D5E38] p-6 bg-white inline-block" data-testid="calendar-widget">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  locale={de}
                  className="rounded-none"
                />
              </div>
            </div>

            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-[#0D5E38]">
                {date ? format(date, "d. MMMM yyyy", { locale: de }) : "Wählen Sie ein Datum"}
              </h3>

              <div
                data-testid="no-events-message"
                className="bg-white border-2 border-[#0D5E38] p-8 text-center"
              >
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 font-semibold">Noch keine Termine geplant.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Wir sind gerade dabei, unsere ersten Treffen zu organisieren. 
                  Neue Termine werden in Kürze hier bekannt gegeben.
                </p>
                <p className="text-sm text-gray-500 mt-3">
                  Interesse? Kontaktiere uns gerne über das Formular unten!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="kontakt" data-testid="contact-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2
            data-testid="contact-title"
            className="text-3xl md:text-5xl font-bold tracking-tight mb-12 text-[#0D5E38] text-center"
          >
            Kontakt
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            <div>
              <div className="bg-[#0D5E38] text-white p-8 border-2 border-[#0D5E38] shadow-[6px_6px_0px_0px_rgba(13,94,56,0.4)] mb-8">
                <h3 className="text-2xl font-bold mb-6">Schreib uns</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5" />
                    <a
                      href="mailto:paris@gruene-paris.de"
                      className="hover:text-[#FBEA0B] transition-colors"
                      data-testid="contact-email-link"
                    >
                      paris@gruene-paris.de
                    </a>
                  </div>
                  <p className="text-white/80 text-sm mt-4">
                    Interesse an einem Austausch über deutsche und europäische Politik? 
                    Melde dich gerne!
                  </p>
                </div>
              </div>

              <div className="bg-[#F3F4F6] p-8 border-l-4 border-[#0D5E38]">
                <h3 className="text-xl font-bold text-[#0D5E38] mb-4">Mehr Informationen</h3>
                <div className="space-y-3">
                  <a
                    href="https://www.instagram.com/gruene_paris"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-800 hover:text-[#0077FF] transition-colors"
                    data-testid="instagram-link"
                  >
                    <Instagram className="w-5 h-5" />
                    <span>@gruene_paris auf Instagram</span>
                  </a>
                  <a
                    href="https://www.gruene.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-800 hover:text-[#0077FF] transition-colors"
                    data-testid="bundesgruene-link"
                  >
                    <ExternalLink className="w-5 h-5" />
                    <span>Bündnis 90/Die Grünen (offizielle Website)</span>
                  </a>
                </div>
              </div>
            </div>

            <div>
              <form
                action="mailto:paris@gruene-paris.de"
                method="GET"
                encType="text/plain"
                data-testid="contact-form"
                className="bg-white border-2 border-[#0D5E38] p-8 shadow-[6px_6px_0px_0px_rgba(13,94,56,0.2)]"
              >
                <h3 className="text-2xl font-bold text-[#0D5E38] mb-6">Kontaktformular</h3>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      data-testid="contact-form-name"
                      className="w-full px-4 py-3 border-2 border-[#0D5E38] focus:outline-none focus:ring-2 focus:ring-[#0077FF] bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                      E-Mail
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="contact-form-email"
                      className="w-full px-4 py-3 border-2 border-[#0D5E38] focus:outline-none focus:ring-2 focus:ring-[#0077FF] bg-white"
                    />
                  </div>

                  <div>
                    <label htmlFor="body" className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                      Nachricht
                    </label>
                    <textarea
                      id="body"
                      name="body"
                      rows="5"
                      required
                      data-testid="contact-form-message"
                      className="w-full px-4 py-3 border-2 border-[#0D5E38] focus:outline-none focus:ring-2 focus:ring-[#0077FF] bg-white resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    className="w-full bg-[#FBEA0B] text-[#0D5E38] font-bold px-8 py-4 text-lg border-2 border-[#0D5E38] shadow-[4px_4px_0px_0px_#0D5E38] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#0D5E38] transition-all uppercase tracking-wide"
                  >
                    Nachricht senden
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Das Formular öffnet Ihr E-Mail-Programm. Alternativ können Sie uns direkt an paris@gruene-paris.de schreiben.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer data-testid="footer" className="bg-[#0D5E38] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold text-lg mb-4">Grüne in Frankreich</h4>
              <p className="text-white/80 text-sm">
                Informeller Austausch für Grünen-Nahestehende in Paris & Île-de-France
              </p>
              <p className="text-white/60 text-xs mt-3">
                Keine offizielle Parteistelle • Privater Zusammenschluss
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Kontakt</h4>
              <a
                href="mailto:paris@gruene-paris.de"
                className="text-white/80 hover:text-[#FBEA0B] transition-colors"
              >
                paris@gruene-paris.de
              </a>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Externe Links</h4>
              <div className="space-y-2">
                <a
                  href="https://www.gruene.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-white/80 hover:text-[#FBEA0B] transition-colors text-sm"
                >
                  Bündnis 90/Die Grünen (offizielle Website)
                </a>
                <a
                  href="https://www.instagram.com/gruene_paris"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-white/80 hover:text-[#FBEA0B] transition-colors text-sm"
                >
                  Instagram @gruene_paris
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20 text-center text-white/60 text-sm">
            <p>&copy; {new Date().getFullYear()} Informelle Initiative • Keine offizielle Verbindung zu Bündnis 90/Die Grünen</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
